import { useEffect } from "react";
import { getSiteUrl } from "./metadata";
import { buildSeoHeadElements } from "./headElements";
import type { PageSeoMeta } from "./types";

type SeoHeadProps = {
	meta: PageSeoMeta;
	siteUrl?: string;
};

function upsertMetaByName(name: string, content: string) {
	let element = document.head.querySelector(`meta[name=\"${name}\"]`) as HTMLMetaElement | null;
	if (!element) {
		element = document.createElement("meta");
		element.setAttribute("name", name);
		document.head.appendChild(element);
	}
	element.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
	let element = document.head.querySelector(
		`meta[property=\"${property}\"]`,
	) as HTMLMetaElement | null;
	if (!element) {
		element = document.createElement("meta");
		element.setAttribute("property", property);
		document.head.appendChild(element);
	}
	element.setAttribute("content", content);
}

function upsertCanonical(url: string) {
	let element = document.head.querySelector("link[rel=\"canonical\"]") as HTMLLinkElement | null;
	if (!element) {
		element = document.createElement("link");
		element.setAttribute("rel", "canonical");
		document.head.appendChild(element);
	}
	element.setAttribute("href", url);
}

function upsertJsonLd(serializedJsonLd?: string) {
	const existing = document.getElementById("seo-jsonld");
	if (!serializedJsonLd) {
		existing?.remove();
		return;
	}
	const script = existing ?? document.createElement("script");
	script.id = "seo-jsonld";
	script.setAttribute("type", "application/ld+json");
	script.textContent = serializedJsonLd;
	if (!existing) {
		document.head.appendChild(script);
	}
}

export default function SeoHead({ meta, siteUrl }: SeoHeadProps) {
	useEffect(() => {
		const resolvedSiteUrl = getSiteUrl(siteUrl || import.meta.env.VITE_SITE_URL || window.location.origin);
		const elements = buildSeoHeadElements(meta, resolvedSiteUrl);
		const jsonLdElement = elements.find((item) => item.type === "script" && item.props.id === "seo-jsonld");

		document.title = meta.title;
		for (const element of elements) {
			if (element.type === "link" && element.props.rel === "canonical" && element.props.href) {
				upsertCanonical(element.props.href);
				continue;
			}
			if (element.type === "meta") {
				if (element.props.name && element.props.content) {
					upsertMetaByName(element.props.name, element.props.content);
				}
				if (element.props.property && element.props.content) {
					upsertMetaByProperty(element.props.property, element.props.content);
				}
			}
		}
		upsertJsonLd(jsonLdElement?.children);
	}, [meta, siteUrl]);

	return null;
}
